import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'

const locale = 'sr-Latn-RS'
const weekdays = ['Pon', 'Uto', 'Sri', 'Čet', 'Pet', 'Sub', 'Ned']

const dateValue = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

function parseDate(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12)
  return Number.isNaN(date.getTime()) ? null : date
}

export function DatePicker({ value, onChange, min, max, required = false, disabled = false, ariaLabel, placeholder = 'Izaberite datum' }: { value: string; onChange: (value: string) => void; min?: string; max?: string; required?: boolean; disabled?: boolean; ariaLabel?: string; placeholder?: string }) {
  const selected = parseDate(value)
  const today = dateValue(new Date())
  const [open, setOpen] = useState(false)
  const [month, setMonth] = useState(() => {
    const base = selected ?? new Date()
    return new Date(base.getFullYear(), base.getMonth(), 1, 12)
  })
  const [style, setStyle] = useState<CSSProperties>({})
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const popoverId = useId()
  const isAllowed = (next: string) => (!min || next >= min) && (!max || next <= max)

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const width = Math.min(316, window.innerWidth - 20)
    const height = 382
    const top = window.innerHeight - rect.bottom >= 290 ? rect.bottom + 7 : Math.max(10, rect.top - height - 7)
    setStyle({ top, left: Math.max(10, Math.min(rect.left, window.innerWidth - width - 10)), width })
  }, [month, open])

  useEffect(() => {
    if (!open) return
    const closeOutside = (event: PointerEvent) => {
      const target = event.target as Node
      if (!triggerRef.current?.contains(target) && !popoverRef.current?.contains(target)) setOpen(false)
    }
    const closeScroll = (event: Event) => {
      if (popoverRef.current && event.composedPath().includes(popoverRef.current)) return
      setOpen(false)
    }
    const closeEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setOpen(false); triggerRef.current?.focus() }
    }
    document.addEventListener('pointerdown', closeOutside)
    document.addEventListener('keydown', closeEscape)
    window.addEventListener('resize', closeScroll)
    window.addEventListener('scroll', closeScroll, true)
    return () => {
      document.removeEventListener('pointerdown', closeOutside)
      document.removeEventListener('keydown', closeEscape)
      window.removeEventListener('resize', closeScroll)
      window.removeEventListener('scroll', closeScroll, true)
    }
  }, [open])

  const start = new Date(month.getFullYear(), month.getMonth(), 1, 12)
  start.setDate(1 - ((start.getDay() + 6) % 7))
  const days = Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start)
    date.setDate(start.getDate() + index)
    return date
  })
  const display = selected ? new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', year: 'numeric' }).format(selected) : placeholder
  const monthLabel = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(month)
  const choose = (next: string) => { onChange(next); setOpen(false); triggerRef.current?.focus() }
  const toggle = () => {
    if (disabled) return
    const base = selected ?? new Date()
    setMonth(new Date(base.getFullYear(), base.getMonth(), 1, 12))
    setOpen((current) => !current)
  }

  return <span className={`platform-date-picker${open ? ' open' : ''}`}>
    <button ref={triggerRef} type="button" className="platform-date-trigger" aria-label={ariaLabel} aria-required={required} aria-haspopup="dialog" aria-expanded={open} aria-controls={popoverId} disabled={disabled} onClick={toggle}><CalendarDays size={16} /><span className={value ? '' : 'placeholder'}>{display}</span><ChevronDown size={15} /></button>
    {open && createPortal(<div ref={popoverRef} id={popoverId} role="dialog" aria-label={ariaLabel ?? 'Izbor datuma'} className="platform-date-popover" style={style}>
      <div className="platform-date-header"><button type="button" aria-label="Prethodni mjesec" onClick={() => setMonth((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1, 12))}><ChevronLeft size={17} /></button><strong>{monthLabel}</strong><button type="button" aria-label="Sljedeći mjesec" onClick={() => setMonth((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1, 12))}><ChevronRight size={17} /></button></div>
      <div className="platform-date-weekdays">{weekdays.map((day) => <span key={day}>{day}</span>)}</div>
      <div className="platform-date-grid">{days.map((date) => {
        const next = dateValue(date)
        return <button type="button" key={next} className={`${date.getMonth() !== month.getMonth() ? 'outside ' : ''}${next === today ? 'today ' : ''}${next === value ? 'selected' : ''}`.trim()} disabled={!isAllowed(next)} aria-label={new Intl.DateTimeFormat(locale, { dateStyle: 'full' }).format(date)} aria-pressed={next === value} onClick={() => choose(next)}>{date.getDate()}</button>
      })}</div>
      <div className="platform-date-footer"><button type="button" disabled={!isAllowed(today)} onClick={() => choose(today)}>Danas</button>{value && !required && <button type="button" onClick={() => choose('')}>Očisti</button>}</div>
    </div>, document.body)}
  </span>
}
